import MyMapsDialog, { IMyMapsDialog } from "./mymaps-dialog";
import { _ } from "~/i18n";
import { createMyMapsInput } from "../mymaps/input";
import { createMyMapsNote } from "../mymaps/note";
import { folderCreate, folderUpdate } from "~/util/providers/mapybox";
import { getMapyboxError, mapyboxNotification } from "~/util/mapybox";
import { MAX_TITLE_LEN } from "./const";
import { checkNoteVersion } from "~/util/user-note";
import { TNoteVersion } from "~/interfaces";
import { openPropositionDialog } from "~/util/premium";
import { createMyMapsToggle } from "~/ui/mymaps/toggle";

interface IState {
	folderTitle: string;
	note: string;
	alwaysOnMap: boolean;
}

interface IMyMapsCreateFolder extends Pick<IMyMapsDialog, "onCancel" | "onClose"> {
	isPremiumUser?: boolean;
	noteVersion?: TNoteVersion;
	onSave?: () => void;
	onError?: () => void;
}

export default class MyMapsCreateFolder {
	_opts: IMyMapsCreateFolder;

	_dialog: MyMapsDialog;

	_state: IState;

	_origState: IState;

	_changed: boolean;

	_titleElem: HTMLInputElement;

	_noteElem: HTMLTextAreaElement;

	constructor(data: IMyMapsCreateFolder) {
		this._opts = {
			onSave: () => {},
			onError: () => {},
			isPremiumUser: false,
			noteVersion: "version1",
			...data,
		};
		this._state = {
			folderTitle: "",
			note: "",
			alwaysOnMap: false,
		};
		this._origState = {
			...this._state,
		};
		this._changed = false;
		this._titleElem = null;
		this._noteElem = null;

		this._build();
	}

	open() {
		this._dialog?.open();
		this._titleElem.focus();
	}

	destroy() {
		this._dialog?.destroy();
	}

	_closeDialog(force?: boolean) {
		this._dialog.close(force);
	}

	_build() {
		const dialog = new MyMapsDialog({
			titles: {
				main: [_("mymap.folders.createFolder")],
				saveBtn: _("mymap.folder.create"),
				cancelBtn: _("cancel"),
			},
			onThrowAwayTest: () => this._checkClose(),
			onThrowAwayConfirm: () => {
				this._closeDialog(true);
			},
			onSave: () => {
				this._onSave();
			},
			onCancel: () => {
				this._closeDialog(true);
			},
		});
		const contents = dialog.contents;
		const mainContent = contents[0];

		mainContent.classList.add("mymaps-dialog__defContent");

		// titulek
		const titleInput = createMyMapsInput({
			placeholder: _("mymap.folder.new"),
			onChange: folderTitle => {
				this._updateState({ folderTitle });
			},
			text: this._state.folderTitle,
			count: MAX_TITLE_LEN,
		});

		mainContent.append(titleInput.container);

		// poznamka
		if (this._opts.isPremiumUser || checkNoteVersion(this._opts.noteVersion, "version2")) {
			const noteData = createMyMapsNote({
				text: this._state.note,
				isPremiumUser: this._opts.isPremiumUser,
				noteVersion: this._opts.noteVersion,
				onChange: note => {
					this._updateState({ note });
				},
			});

			mainContent.append(noteData.container);

			this._noteElem = noteData.textarea;
		}

		// vzdy nad mapou
		if (Mapy.CUS.mapybox.alwaysOnMap) {
			mainContent.appendChild(createMyMapsToggle({
				text: _("mymap.folders.alwaysOnMap"),
				checked: this._opts.isPremiumUser ? this._state.alwaysOnMap || false : false,
				isPremiumUser: this._opts.isPremiumUser,
				onChange: alwaysOnMap => {
					this._updateState({ alwaysOnMap });
				},
				onClick: () => {
					if (!this._opts.isPremiumUser) {
						this._closeDialog(true);
						openPropositionDialog("MyMapsNotes");
					}
				},
			}));
		}

		this._titleElem = titleInput.input;
		this._dialog = dialog;
	}

	_updateState(newData: Partial<IState>) {
		Object.assign(this._state, newData);

		this._changed = this._state.folderTitle !== this._origState.folderTitle
			|| this._state.note !== this._origState.note;
	}

	_checkClose() {
		return !this._changed;
	}

	async _onSave() {
		this._dialog.error = "";
		this._dialog.loading = true;

		try {
			const folderTitle = this._state.folderTitle || _("mymap.folder.new");
			const note = this._state.note.trim();
			const createData = await folderCreate(folderTitle, "", this._state.alwaysOnMap);
			const folderId = createData.id;

			if (note.length > 0) {
				await folderUpdate(folderId, {
					description: note,
				});
			}

			mapyboxNotification({
				operation: "folder-create",
				folderId,
				folderTitle,
			});
			Mapy.getComponent("logger").log("my_maps_folder_create", {
				source: "my_maps",
				isAlwaysOnMap: this._state.alwaysOnMap ? 1 : 0,
			});
		} catch (exc) {
			this._dialog.error = getMapyboxError({
				operation: "folder-create",
			});
			this._dialog.loading = false;
			this._opts.onError();

			return;
		}

		this._opts.onSave();
		this._closeDialog(true);
	}
}
